import { StateCreator } from 'zustand/vanilla';
import { AttributeFilter, BreadcrumbItem, FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { groupByToUniqueAndMutateValue } from 'utils/groupBy';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { REDIRECTS, SKIPPED_BREADCRUMBS } from 'components/breadcrumb/breadcrumbRules';

function getFullPathFromBreadcrumbs(breadcrumbs: BreadcrumbItem[], index: number): string {
  if (index < 0 || index >= breadcrumbs.length) {
    return '';
  }

  return breadcrumbs.slice(0, index + 1).reduce<string>((fullPath, { path }) => {
    if (path.startsWith('/')) {
      return path;
    }
    return `${fullPath}/${path}`;
  }, '');
}

function enrichBreadcrumbsWithFullPaths(breadcrumbs: BreadcrumbItem[]): (BreadcrumbItem & { fullPath: string })[] {
  return breadcrumbs.map((breadcrumb, index) => {
    const fullPath = getFullPathFromBreadcrumbs(breadcrumbs, index);
    return { ...breadcrumb, fullPath };
  });
}

function getNavigationPath(breadcrumbs: BreadcrumbItem[], targetIndex: number): string | null {
  const enriched = enrichBreadcrumbsWithFullPaths(breadcrumbs);
  if (targetIndex >= 0 && targetIndex < enriched.length) {
    const calculatedPath = enriched[targetIndex].fullPath;
    return REDIRECTS[calculatedPath] ?? calculatedPath;
  }
  return null;
}

function getTruncatedBreadcrumbs(breadcrumbs: BreadcrumbItem[], targetIndex: number): BreadcrumbItem[] {
  if (targetIndex >= breadcrumbs.length - 1) {
    return breadcrumbs;
  }

  return breadcrumbs.slice(0, targetIndex + 1);
}

export interface BreadcrumbData {
  breadcrumbs: BreadcrumbItem[];
}

export interface BreadcrumbActions {
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  setDetailPageBreadcrumb: (path: string, filters: AttributeFilter[]) => Record<ExtendedAttributeCode, FilterValue[]>;
  navigateToBreadcrumb: (
    index: number,
    navigate: (path: string) => void | Promise<void>,
    searchParams: URLSearchParams
  ) => void;
  getBreadcrumbsWithFullPaths: () => (BreadcrumbItem & { fullPath: string })[];
}

export interface BreadcrumbSlice extends BreadcrumbData, BreadcrumbActions {}

export const emptyBreadcrumbData: BreadcrumbData = {
  breadcrumbs: [],
};

export { getFullPathFromBreadcrumbs };

export const createBreadcrumbSlice: StateCreator<BreadcrumbSlice, [], [], BreadcrumbSlice> = (set, get) => ({
  ...emptyBreadcrumbData,

  setBreadcrumbs: breadcrumbs =>
    set(() => ({ breadcrumbs: breadcrumbs.filter(bc => !SKIPPED_BREADCRUMBS.find(p => p === bc.path)) })),

  setDetailPageBreadcrumb: (path: string, filters: AttributeFilter[]) => {
    let allFilters: AttributeFilter[] = [];

    set(state => {
      let crumbs;
      const bcIndex = state.breadcrumbs.findIndex(bc => bc.path === path);
      if (bcIndex > -1) {
        crumbs = [...state.breadcrumbs].slice(0, bcIndex + 1);
      } else {
        crumbs = [...state.breadcrumbs, { path, filters }];
      }
      allFilters = crumbs.flatMap(c => (c.filters ? c.filters : []));
      return {
        breadcrumbs: crumbs,
      };
    });
    return groupByToUniqueAndMutateValue(
      allFilters,
      item => item.attribute,
      item => item.values
    );
  },

  navigateToBreadcrumb: (index, navigate, searchParams: URLSearchParams) => {
    const { breadcrumbs } = get();
    let navigationPath = getNavigationPath(breadcrumbs, index);

    const filters = searchParams.get('state');
    const accountNumber = searchParams.get('accountNumber');

    const pathElements: string[] = [];

    if (filters) {
      pathElements.push(`state=${filters}`);
    }
    if (accountNumber) {
      pathElements.push(`accountNumber=${accountNumber}`);
    }

    if (pathElements.length > 0) {
      navigationPath = `${navigationPath}?${pathElements.join('&')}`;
    }

    if (navigationPath) {
      navigate(navigationPath);
      const truncatedBreadcrumbs = getTruncatedBreadcrumbs(breadcrumbs, index);
      set(() => ({ breadcrumbs: truncatedBreadcrumbs }));
    }
  },

  getBreadcrumbsWithFullPaths: () => {
    const { breadcrumbs } = get();
    return enrichBreadcrumbsWithFullPaths(breadcrumbs);
  },
});
